import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, Loader2, X } from "lucide-react";
import { runJob, useApi } from "@/lib/api";

const INFLIGHT = new Map();
const fetchCadangan = (api, id) => {
  if (!INFLIGHT.has(id)) {
    INFLIGHT.set(id, (async () => {
      const r = await api("post", `/hte/kajian/${id}/cadangan`, {});
      return r.cadangan || runJob(api, `/hte/kajian/${id}/cadangan`, null, undefined, r.job_id);
    })().catch((e) => { INFLIGHT.delete(id); throw e; }));
  }
  return INFLIGHT.get(id);
};

const NAMA = { bdq: "BDQ", skda: "SKDA", kbm: "KBM", adab: "ADAB" };

const Section = ({ title, children, testid }) => (
  <div className="space-y-2" data-testid={testid}><div className="eyebrow">{title}</div>{children}</div>
);

export const CadanganPanel = ({ kajian, auto }) => {
  const api = useApi();
  const [cad, setCad] = useState(kajian.cadangan || null);
  const [state, setState] = useState(kajian.cadangan ? "ok" : auto ? "loading" : "none");
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (kajian.cadangan || !auto) return;
    let alive = true;
    (async () => {
      try {
        const c = await fetchCadangan(api, kajian.id);
        if (alive) { setCad(c); setState("ok"); }
      } catch { if (alive) setState("gagal"); }
    })();
    return () => { alive = false; };
  }, [api, kajian.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open || state === "none") return null;
  return (
    <aside className="surface rounded-2xl border border-[var(--q)] p-5 sm:p-6 space-y-5 fade-up relative" data-testid="cadangan-panel">
      <button onClick={() => setOpen(false)} aria-label="Tutup cadangan" data-testid="cadangan-close" className="absolute right-3 top-3 h-9 w-9 grid place-items-center rounded-full text-mute hover:text-ink hover:bg-bg"><X size={16} /></button>
      <div className="flex items-center gap-2 text-sm font-semibold text-q"><Compass size={16} /> Cadangan Agent · langkah seterusnya</div>
      {state === "loading" && <div className="flex items-center gap-2 text-sm text-mute" data-testid="cadangan-loading"><Loader2 size={15} className="animate-spin" /> Menyediakan cadangan… (percuma, tiada kuota)</div>}
      {state === "gagal" && <p className="text-sm text-mute" data-testid="cadangan-error">Cadangan tidak dapat disediakan buat masa ini.</p>}
      {state === "ok" && cad && <>
        <Section title="Terapkan metodologi pada ayat ini" testid="cadangan-metodologi">
          <div className="grid md:grid-cols-2 gap-3">
            {cad.metodologi.map((m) => (
              <div key={m.kod} className="rounded-xl border border-line p-4" data-testid={`cadangan-metodologi-${m.kod}`}>
                <div className="font-display text-base text-ink">{NAMA[m.kod]}</div>
                <p className="text-sm text-ink/90 mt-1 leading-relaxed">{m.panduan}</p>
              </div>
            ))}
          </div>
        </Section>
        {cad.ayat.length > 0 && <Section title="Ayat lain yang berkaitan (teks disahkan daripada pangkalan data)" testid="cadangan-ayat">
          <ul className="grid md:grid-cols-2 gap-3">
            {cad.ayat.map((a) => (
              <li key={a.rujukan} className="rounded-xl border border-line p-4 text-sm" data-testid={`cadangan-ayat-${a.surah}-${a.ayat}`}>
                <div className="flex justify-between gap-2"><strong className="text-ink">{a.rujukan}</strong>
                  <Link to={`/hte?ref=${a.surah}:${a.ayat}`} className="text-xs text-q underline underline-offset-4 shrink-0" data-testid={`cadangan-ayat-kaji-${a.surah}-${a.ayat}`}>Kaji ayat ini</Link></div>
                <p className="text-ink/90 mt-1 line-clamp-3">{a.terjemahan}</p>
                {a.kaitan && <p className="text-xs text-mute mt-2">{a.kaitan}</p>}
              </li>
            ))}
          </ul>
        </Section>}
        <Section title="Tindakan seterusnya" testid="cadangan-tindakan">
          <ul className="list-disc pl-5 text-sm text-ink space-y-1">{cad.tindakan.map((t, i) => <li key={i}>{t}</li>)}</ul>
          <div className="flex flex-wrap gap-2 pt-1">
            <Link to="/program" className="h-9 px-4 rounded-full bg-ink text-bg text-sm font-semibold inline-flex items-center" data-testid="cadangan-to-program">Gunakan dalam program latihan</Link>
          </div>
        </Section>
        <p className="text-[11px] text-mute">Cadangan dijana AI — semak sebelum digunakan.</p>
      </>}
    </aside>
  );
};
